<script setup lang="ts">
// TODO :: Review this entire component logics and validations
import { computed, ref } from 'vue';
import { dateRelation, formatMoney, getDefaultDateFormat, formatPhoneNumber, getDateFromOId } from '@Helpers';
import { FORMAT_COMPONENTS } from '@DataTableModule';

const props = defineProps<{
  field: any;
  rowData: any;
  settings: any;
}>();

const {
  ADOPTION_FEE,
  BIRTHDAY,
  CONCATENATE,
  DATE_FROM_OID,
  DATE_TIME_TO_DATE,
  DATE_STRING_WITH_TIME,
  DATE_TO_ISO_STRING,
  DAYS_TO_FIXED,
  EXTRA_INFO,
  MONEY,
  NUMBER_TO_BIRTHDAY,
  NUMBER_TO_DATE,
  PERCENTAGE,
  PHONE,
  OBJECT_MODEL,
  NEW_PLATFORM,
} = FORMAT_COMPONENTS;

const extraInfoPanel = ref();

const type = computed(() => props.settings?.render?.type);
const isNegativeValue = computed(() => (sanitizedValue.value < 0 ? 'text-red-500' : ''));
const isProductsInfo = computed(() => props.settings.type === 'products');
const isServicesInfo = computed(() => props.settings.type === 'services');
const hasDolar = computed(() => (props.settings.type === 'money' ? '$' : ''));
const extraInfos = computed(() =>
  // prettier-ignore
  isServicesInfo.value ? props.rowData.services :
  isProductsInfo.value ? props.rowData.products :
  props.rowData.additional_fees
);
const checkForInfoLength = computed(() =>
  // prettier-ignore
  isProductsInfo.value ? props.rowData.products.length : 
  isServicesInfo.value ? props.rowData.services.length : 
  props.rowData.additional_fees.length
);

const sanitizedValue = computed(() => {
  const value =
    props.settings.render.targetKey &&
    props.settings.render.targetKey.length > 0 &&
    props.field[props.settings.render.targetKey[0]] !== undefined
      ? props.field[props.settings.render.targetKey[0]]
      : props.field;

  return Number(value) === 0 ? Math.abs(Number(value)) : value;
});

const isDateFromId = computed(() => {
  const notValidType = type.value !== DATE_FROM_OID;
  if (notValidType) return false;

  const hasOId = props.settings.render.targetKey.includes('$oid');
  return hasOId;
});

function toggle(event: any) {
  extraInfoPanel.value.toggle(event);
}
</script>

<template>
  <p v-if="type === DAYS_TO_FIXED">{{ field?.toFixed(2) }} {{ $t('common.day', field) }}</p>

  <p v-else-if="type === NUMBER_TO_DATE && field">
    {{ getDefaultDateFormat(field?.[settings.render.targetKey?.[0]]?.[settings.render.targetKey?.[1]] || field) }}
  </p>

  <div
    v-else-if="type === NUMBER_TO_BIRTHDAY && field"
    class="flex flex-col"
  >
    <span>
      {{ getDefaultDateFormat(field[settings.render.targetKey[0]][settings.render.targetKey[1]]) }}
    </span>

    <span>
      {{
        dateRelation(
          getDefaultDateFormat(field[settings.render.targetKey[0]][settings.render.targetKey[1]] || '-') || '-'
        )
      }}
    </span>
  </div>

  <p v-else-if="type === PHONE">
    {{ !field?.length ? '-' : formatPhoneNumber(Array.isArray(field) ? field[0] : field) }}
  </p>

  <p v-else-if="type === CONCATENATE">
    {{
      !field && !rowData[settings.concatenateTarget]
        ? '-'
        : (field || '0') + ' ' + (rowData[settings.concatenateTarget] || '')
    }}
  </p>

  <p v-else-if="type === OBJECT_MODEL">{{ field?.value || field || '-' }}</p>

  <div
    v-else-if="type === EXTRA_INFO"
    class="flex items-center justify-between gap-3"
  >
    <p :class="isNegativeValue">
      {{ field.length > 1 ? `${hasDolar + field[0]}, ${hasDolar + field[1]} ...` : hasDolar + field[0] }}
    </p>

    <Button
      v-if="field.length > 1"
      outlined
      class="w-9 border-none"
      icon="pi pi-info-circle"
      severity="secondary"
      @click="toggle"
    />

    <OverlayPanel ref="extraInfoPanel">
      <div
        v-for="(item, index) in field"
        class="flex gap-2 border-t py-1 text-sm first:border-t-0 first:pt-0 last:pb-0"
        :key="index"
      >
        <p
          v-if="settings.type === 'quantity'"
          class="font-medium"
        >
          {{ '(' + field[index] + ') ' + rowData.sku[index] }}
        </p>

        <p
          v-else-if="settings.type === 'money'"
          class="font-medium"
        >
          {{ '(' + rowData.quantity[index] + ') ' + rowData.sku[index] + ' $' + field[index] }}
        </p>

        <p
          v-else
          class="font-medium"
        >
          {{ '(' + rowData.quantity[index] + ') ' + item }}
        </p>
      </div>
    </OverlayPanel>
  </div>

  <div
    v-else-if="type === MONEY && field"
    class="flex items-center justify-end gap-3"
  >
    <Button
      v-if="settings.extraInfo && checkForInfoLength"
      outlined
      class="w-9 border-none"
      icon="pi pi-info-circle"
      severity="secondary"
      @click="toggle"
    />

    <p :class="[{ 'text-red-500': sanitizedValue < 0 || settings?.render?.negative }]">
      ${{
        sanitizedValue.value < 0 || settings?.render?.negative
          ? `(${formatMoney(Math.abs(sanitizedValue))})`
          : formatMoney(sanitizedValue)
      }}
    </p>

    <OverlayPanel ref="extraInfoPanel">
      <div
        v-for="({ amount, description, price, product, quantity }, index) in extraInfos"
        class="flex gap-2 border-t py-1 text-sm first:border-t-0 first:pt-0 last:pb-0"
        :key="index"
      >
        <p class="font-medium">{{ isProductsInfo || isServicesInfo ? `(${quantity}) ` + product : description }}:</p>

        <p>${{ isProductsInfo || isServicesInfo ? price : formatMoney(amount.$numberDecimal) }}</p>
      </div>
    </OverlayPanel>
  </div>

  <div v-else-if="type === DATE_TO_ISO_STRING && field">{{ getDefaultDateFormat(field) }}</div>

  <div
    v-else-if="type === BIRTHDAY && field"
    class="flex flex-col"
  >
    <span>{{ getDefaultDateFormat(field) }}</span>

    <span>{{ dateRelation(field) }}</span>
  </div>

  <p v-else-if="isDateFromId">{{ getDateFromOId(rowData._id.$oid) }}</p>

  <p v-else-if="type === DATE_STRING_WITH_TIME">
    {{ getDefaultDateFormat(field, true) }}
  </p>

  <p v-else-if="type === DATE_TIME_TO_DATE">{{ getDefaultDateFormat(field) }}</p>

  <p v-else-if="type === ADOPTION_FEE && field">${{ formatMoney(field) }}</p>

  <p v-else-if="type === ADOPTION_FEE && !field">-</p>

  <p v-else-if="type === NEW_PLATFORM">{{ field ? 'Yes' : ' - ' }}</p>

  <p v-else-if="type === PERCENTAGE && field">{{ field + '%' || ' - ' }}</p>

  <span
    v-else-if="type === MONEY && !field"
    class="flex flex-col items-end gap-0.5"
  >
    $0.00
  </span>

  <span
    v-else
    class="flex flex-col items-start gap-0.5"
  >
    -
  </span>
</template>
